import { useEffect, useState, type FormEvent } from 'react';
import { MarcaIcone } from '@shared/components/ui/MarcaIcone';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, rotas } from '@shared/lib/api';
import { useSessao } from '@shared/auth/sessao';
import type {
  CadastroEnviado, MeuCadastro, Rede, RedeSocial,
} from '@shared/types/api';
import { SeletorRede } from '@shared/components/ui/SeletorRede';
import { SeletorPais } from '@shared/components/ui/SeletorPais';
import { OUTRO, PAIS_PADRAO, acharPais, paisDoE164 } from '@shared/components/ui/paises';
import s from './Cadastro.module.css';

function useMeuCadastro() {
  return useQuery<MeuCadastro>({
    queryKey: ['eu', 'cadastro'],
    queryFn: () => api.get<MeuCadastro>(rotas.meuCadastro),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function useEnviarCadastro() {
  const qc = useQueryClient();
  return useMutation<MeuCadastro, Error, CadastroEnviado>({
    mutationFn: (d) => api.put<MeuCadastro>(rotas.meuCadastro, d),
    // NESTA ORDEM, e esperando: a sessao primeiro. O status virou
    // em_analise, e a pagina sai para a home quando o cadastro recarregar;
    // se a sessao ainda tivesse o status antigo, o roteador veria
    // "cadastro incompleto" e mandaria de volta para ca.
    onSuccess: async () => {
      await qc.refetchQueries({ queryKey: ['auth', 'me'] });
      await qc.invalidateQueries({ queryKey: ['eu', 'cadastro'] });
    },
  });
}

/** CPF 000.000.000-00 | CNPJ 00.000.000/0000-00, enquanto digita. */
function formataDoc(v: string, pessoa: string): string {
  const n = v.replace(/\D/g, '').slice(0, pessoa === 'juridica' ? 14 : 11);
  if (pessoa === 'juridica') {
    return n
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return n
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

/** So digitos, limitado ao maior tamanho que o pais aceita.
 *  Sem mascara: o formato local varia demais entre paises para valer a
 *  pena, e o exemplo no placeholder ja orienta. */
function limpaTel(v: string, iso: string): string {
  const max = Math.max(...acharPais(iso).tamanhos);
  return v.replace(/\D/g, '').slice(0, max);
}

/** Redes oferecidas no combo. A ordem e a de uso esperado, nao alfabetica. */
const REDES: Array<{ valor: Rede; rotulo: string }> = [
  { valor: 'youtube',   rotulo: 'YouTube' },
  { valor: 'instagram', rotulo: 'Instagram' },
  { valor: 'x',         rotulo: 'X (Twitter)' },
  { valor: 'tiktok',    rotulo: 'TikTok' },
  { valor: 'telegram',  rotulo: 'Telegram' },
  { valor: 'discord',   rotulo: 'Discord' },
  { valor: 'twitch',    rotulo: 'Twitch' },
  { valor: 'linkedin',  rotulo: 'LinkedIn' },
  { valor: 'facebook',  rotulo: 'Facebook' },
  { valor: 'site',      rotulo: 'Site' },
];

/**
 * Lista de redes sociais, opcional.
 *
 * Uma entrada por rede: o banco tem unique (user_id, rede), entao o combo
 * esconde as ja escolhidas em vez de deixar o usuario montar um estado
 * que o servidor vai recusar.
 */
function CamposRedes({ redes, aoMudar }: {
  redes: RedeSocial[];
  aoMudar: (r: RedeSocial[]) => void;
}) {
  const usadas = new Set(redes.map((r) => r.rede));
  const livres = REDES.filter((r) => !usadas.has(r.valor));

  function trocar(i: number, campo: 'rede' | 'handle', valor: string) {
    const copia = [...redes];
    copia[i] = { ...copia[i], [campo]: valor } as RedeSocial;
    aoMudar(copia);
  }

  return (
    <div className={s.campo}>
      <div className={s.rotulo}>Redes sociais</div>

      {redes.length === 0 && (
        <div className={s.vazioRedes}>Nenhuma rede informada.</div>
      )}

      <div className={s.redes}>
        {redes.map((r, i) => {
          const def = REDES.find((x) => x.valor === r.rede);
          return (
            <div key={i} className={s.redeLinha}>
              <SeletorRede
                valor={r.rede}
                aoMudar={(v) => trocar(i, 'rede', v)}
                // Todas as redes aparecem; as ja usadas em outra linha
                // ficam desabilitadas. Some-las esconderia o que existe e
                // deixaria o usuario sem entender por que a lista encolhe.
                opcoes={REDES.map((x) => ({
                  valor: x.valor,
                  rotulo: x.rotulo,
                  indisponivel: x.valor !== r.rede && usadas.has(x.valor),
                }))}
              />
              <input className={s.entrada} value={r.handle}
                     onChange={(e) => trocar(i, 'handle', e.target.value)}
                     placeholder="Nome ou Link" />
              <button type="button" className={s.remover}
                      aria-label={`Remover ${def?.rotulo ?? 'rede'}`}
                      onClick={() => aoMudar(redes.filter((_, j) => j !== i))}>
                {'×'}
              </button>
            </div>
          );
        })}
      </div>

      <button type="button" className={s.adicionar} disabled={livres.length === 0}
              onClick={() => aoMudar([...redes, { rede: livres[0].valor, handle: '' }])}>
        {livres.length === 0 ? 'Todas as redes adicionadas' : '+ Adicionar rede'}
      </button>
    </div>
  );
}

export function PaginaCadastro() {
  const { data: cadastro, isPending, isError, error } = useMeuCadastro();
  const enviar = useEnviarCadastro();
  const { sair } = useSessao();

  const [pessoa, setPessoa] = useState<'fisica' | 'juridica'>('fisica');
  const [documento, setDocumento] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomePublico, setNomePublico] = useState('');
  const [paisTel, setPaisTel] = useState(PAIS_PADRAO);
  // So usado quando o pais e OUTRO: o DDI vem digitado.
  const [ddiManual, setDdiManual] = useState('');
  const [telefone, setTelefone] = useState('');
  const [redes, setRedes] = useState<RedeSocial[]>([]);

  // Preenche com o que ja existe -- importante em quem teve o cadastro
  // recusado e volta para corrigir: refazer tudo do zero seria hostil.
  useEffect(() => {
    if (!cadastro) return;
    if (cadastro.pessoa) setPessoa(cadastro.pessoa);
    if (cadastro.documento) {
      setDocumento(formataDoc(cadastro.documento, cadastro.pessoa ?? 'fisica'));
    }
    setRazaoSocial(cadastro.razao_social ?? '');
    setNomePublico(cadastro.nome_publico ?? cadastro.nome ?? '');
    if (cadastro.telefone) {
      const { pais, local, ddi } = paisDoE164(cadastro.telefone);
      setPaisTel(pais.iso);
      setDdiManual(pais.iso === OUTRO ? ddi : '');
      setTelefone(local);
    }
    setRedes(cadastro.redes ?? []);
  }, [cadastro]);

  if (isPending) {
    return <div className={`${s.tela} shell`}><div className={s.centro}>
      <div className={s.cartao}><div className={s.sub}>Carregando…</div></div>
    </div></div>;
  }

  if (isError) {
    return <div className={`${s.tela} shell`}><div className={s.centro}>
      <div className={s.cartao}>
        <div className={`${s.faixa} ${s.faixaErro}`}>
          {(error as Error).message}
        </div>
      </div>
    </div></div>;
  }

  // Ja enviado (ou ja aprovado): nada a preencher aqui. Vai para a home,
  // que avisa da analise numa faixa -- antes ficava preso numa tela de
  // "aguarde" so com o botao de sair (ver precisaCompletarCadastro).
  if (cadastro.status === 'em_analise' || cadastro.status === 'ativo') {
    return <Navigate to="/" replace />;
  }

  function submeter(e: FormEvent) {
    e.preventDefault();
    enviar.mutate({
      pessoa,
      documento: documento.replace(/\D/g, ''),
      razao_social: pessoa === 'juridica' ? razaoSocial : null,
      nome_publico: nomePublico,
      slug: null,                    // o servidor gera do nome publico
      telefone: `+${paisTel === OUTRO ? ddiManual : acharPais(paisTel).ddi}${telefone}`,
      // O email de login ja identifica o provedor; um segundo
      // endereco so criaria dois lugares para manter atualizados.
      email_contato: null,
      // Linha adicionada e deixada em branco nao vira cadastro.
      redes: redes.filter((r) => r.handle.trim()),
    });
  }

  return (
    <div className={`${s.tela} shell`}>
      <div className={s.centro}>
        <div className={s.marca}>
          <MarcaIcone tamanho={32} className={s.ponto} />
          <span className={s.nome}>Heavy Trade</span>
        </div>

        <div className={s.cartao}>
          <h1 className={s.titulo}>Complete seu cadastro</h1>

          {cadastro.status === 'rejeitado' && cadastro.motivo_recusa && (
            <div className={`${s.faixa} ${s.faixaAviso}`}>
              <strong>Cadastro recusado.</strong><br />
              {cadastro.motivo_recusa}
            </div>
          )}

          <form onSubmit={submeter}>
            <div className={s.rotulo}>Tipo de pessoa</div>
            <div className={s.escolha}>
              {(['fisica', 'juridica'] as const).map((t) => (
                <button key={t} type="button"
                        className={pessoa === t ? `${s.opcao} ${s.opcaoAtiva}` : s.opcao}
                        onClick={() => { setPessoa(t); setDocumento(''); }}>
                  {t === 'fisica' ? 'Pessoa física' : 'Pessoa jurídica'}
                </button>
              ))}
            </div>

            <div className={s.campo}>
              <label className={s.rotulo} htmlFor="doc">
                {pessoa === 'juridica' ? 'CNPJ' : 'CPF'}
              </label>
              <input id="doc" className={s.entrada} required inputMode="numeric"
                     value={documento}
                     onChange={(e) => setDocumento(formataDoc(e.target.value, pessoa))}
                     placeholder={pessoa === 'juridica' ? '00.000.000/0000-00' : '000.000.000-00'} />
            </div>

            {pessoa === 'juridica' && (
              <div className={s.campo}>
                <label className={s.rotulo} htmlFor="razao">Razão social</label>
                <input id="razao" className={s.entrada} required
                       value={razaoSocial}
                       onChange={(e) => setRazaoSocial(e.target.value)} />
              </div>
            )}

            <div className={s.campo}>
              <label className={s.rotulo} htmlFor="publico">Nome público</label>
              <input id="publico" className={s.entrada} required minLength={2}
                     value={nomePublico}
                     onChange={(e) => setNomePublico(e.target.value)} />
              <div className={s.dica}>
                É o nome que aparece para os clientes.
              </div>
            </div>

            {/* O telefone ocupa a linha inteira: dividi-la com o email
                deixava o numero em ~120px, apertado demais para 11
                digitos mais o seletor de pais. */}
            <div className={s.campo}>
              <label className={s.rotulo} htmlFor="tel">Telefone</label>
              <div className={paisTel === OUTRO ? s.telLinhaOutro : s.telLinha}>
                <SeletorPais valor={paisTel} aoMudar={(iso) => {
                  setPaisTel(iso);
                  // Trocar de pais pode encurtar o limite: reaplica.
                  setTelefone((t) => limpaTel(t, iso));
                }} />
                {paisTel === OUTRO && (
                  <input className={s.entrada} required inputMode="numeric"
                         value={ddiManual}
                         onChange={(e) =>
                           setDdiManual(e.target.value.replace(/\D/g, '').slice(0, 4))}
                         placeholder="DDI"
                         aria-label="Código do país" />
                )}
                <input id="tel" className={s.entrada} required inputMode="numeric"
                       value={telefone}
                       onChange={(e) => setTelefone(limpaTel(e.target.value, paisTel))}
                       placeholder={acharPais(paisTel).exemplo} />
              </div>
            </div>

            <CamposRedes redes={redes} aoMudar={setRedes} />

            {enviar.isError && (
              <div className={`${s.faixa} ${s.faixaErro}`}>
                {(enviar.error as Error).message}
              </div>
            )}

            <button className={s.botao} type="submit" disabled={enviar.isPending}>
              {enviar.isPending ? 'Enviando…' : 'Enviar para análise'}
            </button>
          </form>

          <div className={s.rodape}>
            Entrou como {cadastro.email} ·{' '}
            <button className={s.sair} onClick={() => void sair()}>Sair</button>
          </div>
        </div>
      </div>
    </div>
  );
}
